import PageHeader from "@/components/PageHeader";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { CtaBanner } from "@/components/CTA";
import { Section } from "@/pages/Home";
import Seo from "@/components/Seo";
import Pathway from "@/components/Pathway";
import Testimonials from "@/components/Testimonials";
import { IMG, ImageBreak } from "@/components/ImageAssets";
import { Link } from "react-router-dom";
import { CheckCircle2, Handshake, BarChart3, Lock } from "lucide-react";

const modelSteps = [
  { title: "Voice-native intake with WHO Guides", body: "Start with the human voice — a person speaks a real need. A real WHO Guide activates the agentic system: nine specialist AI agents coordinate quietly in the background." },
  { title: "Needs identification across nine domains", body: "The orchestrator maps every service category that could help — Healthcare, Benefits, Housing, Employment, Education, Transportation, Family, Community, Financial. Not just the obvious one." },
  { title: "Geo-located resource matching", body: "Nearby, verified, eligibility-aware options — matched by specialist agents to the actual need." },
  { title: "Verified provider network", body: "Trusted, credentialed partners — accountable to shared standards." },
  { title: "Secure digital referral", body: "With context, consent, and role-based access from the first minute." },
  { title: "Direct partner-to-partner handoff", body: "The receiving organization knows the person is coming — and by whom." },
  { title: "Agentic follow-up", body: "Accepted, contacted, completed — specialist agents keep the loop warm and the WHO Guide steps in whenever a human is needed." },
  { title: "Outcome reporting", body: "Understand what actually happened, so systems can improve." },
];

export default function Solution() {
  return (
    <div data-testid="solution-page">
      <PilotDemoBanner />
      <Seo
        title="The Solution | WHO Guides™"
        description="How WHO turns fragmented referrals into accountable warm handoffs. A person speaks one need. A real WHO Guide, supported by nine specialist AI agents, coordinates verified partners, makes the handoff, and stays with the person until the connection is confirmed."
        path="/solution"
      />
      <PageHeader
        eyebrow="The Solution"
        title="Say what you need. WHO Guides walk you through it."
        lede="A person speaks one need. A real WHO Guide, supported by nine specialist AI agents (Healthcare, Benefits, Housing, Employment, Education, Transportation, Family, Community, Financial), coordinates verified partners, makes warm handoffs, and stays in the loop until every connection is confirmed."
      />

      <Section eyebrow="A better way to connect people to support" title="From spoken need to confirmed connection.">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-lg text-slateSoft leading-relaxed">
              WHO helps organizations coordinate around the person instead of forcing the person to
              coordinate among dozens of organizations. The Relationship Operating System starts
              with voice: a person speaks, a <span className="text-navy font-semibold">WHO Guide</span>
              &nbsp;answers, and the warm handoff actually happens — intentional, secure, tracked, and verified.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "One spoken sentence, not a dozen phone trees",
                "One WHO Guide who owns the moment help is needed",
                "One provider matched to the actual need — introduced warmly",
                "One shared record of what was promised and what happened",
                "One accountable network — visible to funders, partners, and the person",
              ].map((it) => (
                <li key={it} className="flex gap-3 text-navy" data-testid={`solution-intro-${it.toLowerCase().replace(/\W+/g, "-").slice(0, 40)}`}>
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-gold" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-borderSubtle shadow-md">
              <img
                src="/assets/solution-network.jpeg"
                alt="A veteran at the center surrounded by connected providers — healthcare, housing, employment, education, family support, and community — through verified warm handoffs"
                className="w-full h-[420px] object-cover"
                loading="lazy"
                data-testid="solution-network-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-cream">
                <p className="eyebrow text-gold-light">One person · Every provider</p>
                <p className="mt-1 font-display font-bold text-lg leading-tight">Coordinated care, verified handoffs.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="The WHO Model" title="Eight moments. One accountable pathway." bg="bone">
        <div className="relative">
          <div className="hidden lg:block">
            <Pathway nodes={8} />
          </div>
          <ol className="grid lg:grid-cols-2 gap-x-24 gap-y-8 relative">
            {modelSteps.map((s, i) => (
              <li
                key={s.title}
                data-testid={`model-step-${i + 1}`}
                className={`bg-white border border-borderSubtle rounded-lg p-6 ${i % 2 === 0 ? "lg:mr-8" : "lg:ml-8 lg:mt-14"}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-md bg-navy-900 text-white font-display font-bold text-sm inline-flex items-center justify-center">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display font-bold text-navy-900 text-lg">{s.title}</h3>
                </div>
                <p className="mt-3 text-slateSoft leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      <Section eyebrow="Built for real-world systems" title="One platform. Every partner who serves the person.">
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            "Veteran service orgs", "Healthcare systems", "Workforce boards", "Public agencies",
            "Colleges & universities", "Nonprofits", "Community providers", "Philanthropic funders",
          ].map((s) => (
            <div key={s} className="p-4 bg-white border border-borderSubtle rounded-md text-navy-900 font-medium" data-testid={`solution-audience-${s.toLowerCase().replace(/\W+/g, "-")}`}>
              {s}
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Designed for accountability, trust, and impact" title="Every referral has a status. Every partner has a role." bg="bone">
        <div className="grid lg:grid-cols-3 gap-6">
          <PillarCard
            icon={<CheckCircle2 />}
            title="Accountability"
            body="See whether each referral was accepted, contacted, completed, pending, or unresolved."
            list={["Referrals made", "Handoffs accepted", "Time to connection", "Service completion", "Unmet needs"]}
          />
          <PillarCard
            icon={<Lock />}
            title="Trust"
            body="Privacy by design — role-based access, secure communication, and consent-based information sharing."
            list={["Verified partner participation", "Consent-first data sharing", "Role-based access", "Secure messaging"]}
          />
          <PillarCard
            icon={<BarChart3 />}
            title="Impact"
            body="Measurable outcomes for people, partners, funders, and public agencies — not just activity."
            list={["Resource gaps", "Partner performance", "Community impact", "System-level learning"]}
          />
        </div>
      </Section>

      <Section eyebrow="A shared commitment" title="Every referral has a home. Every person has an advocate.">
        <div className="mt-4 p-10 lg:p-14 bg-navy-900 text-white rounded-lg text-center" data-testid="solution-closing">
          <Handshake className="w-10 h-10 text-gold mx-auto" />
          <p className="mt-6 font-display font-black text-3xl lg:text-4xl leading-[1.1]">
            WHO Guides stay with the person<br />
            until the handoff becomes a connection.
          </p>
          <p className="mt-6 text-white/80 max-w-2xl mx-auto leading-relaxed">
            Curious how every handoff turns into a measured outcome? See the operations, executive, and
            network views &mdash; and the full accountability loop &mdash; on{" "}
            <Link to="/impact" data-testid="solution-impact-inline-link" className="text-gold underline decoration-white/20 hover:decoration-gold underline-offset-4">
              our Impact page
            </Link>.
          </p>
        </div>
      </Section>

      <Testimonials eyebrow="Partner Voices" title="What early pilot partners are seeing." />

      <CtaBanner
        eyebrow="See the numbers behind the model."
        title="Explore how WHO measures every warm handoff."
        primaryText="See the Impact"
        primaryTo="/impact"
        secondaryText="Become a Partner"
        secondaryTo="/partners"
      />
    </div>
  );
}

function PillarCard({ icon, title, body, list }) {
  return (
    <div className="bg-white border border-borderSubtle rounded-lg p-7 flex flex-col" data-testid={`pillar-${title.toLowerCase()}`}>
      <span className="inline-flex w-11 h-11 items-center justify-center rounded-md bg-navy text-gold">{icon}</span>
      <h3 className="mt-5 font-display font-bold text-navy-900 text-xl">{title}</h3>
      <p className="mt-2 text-slateSoft leading-relaxed">{body}</p>
      <ul className="mt-5 space-y-2 text-sm">
        {list.map((l) => (
          <li key={l} className="flex gap-2 text-navy-900">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
            {l}
          </li>
        ))}
      </ul>
    </div>
  );
}
